/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-05 16:48:52                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-10 17:21:33                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * Components
 * Services
 * AdminCompinfos: Admin compinfos view
 * useEffect: get compinfos list
 * getInputCompInfos: Get compinfos input list
 * getSelectCompInfos: Get compinfos select list
 * mergeCompInfos: Merge input and select compinfos
 */

/* Imports */
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Table } from "../../../../../components/table/Table";
import CompInfosListItem from "../../../../../components/data/compinfos/CompInfosListItem";
import Loading from "../../../../../components/loading/Loading";
import { ComponentType, useEffect, useState } from "react";
import { t } from "i18next";
import { api } from "../../../../../services/api/api";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../../../../services/api/error";
import CompInfoForm from "../../../../../components/data/compinfo/CompInfoForm";
import { useDrawerLayoutDispatch } from "../../../../../components/drawer-layout/DrawerLayout";
import { Button, Typography } from "@material-tailwind/react";
import Switch from "../../../../../components/switch/Switch";

/***/

/* Components */
/***/

/* Services */
/***/

/**
* DrawerForm - drawer form container component
* @param data - form data
* @param FormComponent - form sub component
* @param onSubmit - submit callback
* @param closeDrawer - close drawer function
*/
function DrawerForm<T>({
  data,
  FormComponent,
  onSubmit,
  closeDrawer
}: {
  data: T,
  onSubmit: (data: T) => void,
  closeDrawer: () => void,
  FormComponent: ComponentType<{
    data?: T,
    onChange: (data: T) => void,
    onSubmit: () => void,
    submitted?: boolean
  }>
}) {
  const [dataState, setDataState] = useState<T>(data);
  const [submitted, setSubmitted] = useState<boolean>(false);

  /**
  * formChange - handle form change
  * @param name - object key name
  * @param value - object key value
  */
  const formChange = (data: T) => {
    setDataState(data);
  };
  /***/

  /**
  * submitForm: trigger form submit event 
  */
  const submitForm = () => {
    setSubmitted(true);
    onSubmit(dataState);
  };
  /***/

  return (
    <div className="drawer-form">
      <div className="content">
        <FormComponent  data={dataState}
                        onChange={formChange}
                        onSubmit={() => onSubmit(dataState)}
                        submitted={submitted}/>
      </div>


      <div className="footer">
        <div className="text-xs text-gray-400 italic">{t("common:mandatoryField")}</div>

        <Button size="sm" variant="outlined" onClick={closeDrawer}>
          {t("common:cancel")}
        </Button>

        <Button size="sm"color="blue" onClick={() => submitForm()}>
          {t("common:validate")}
        </Button>
      </div>
    </div>
  );
}
/***/

/**
 * AdminCompinfos: Admin compinfos view
 */
export default function Compinfos({
  compInfos,
  onCompInfosChange,
  onChange,
  isReordering
} : {
  compInfos: EasyTech.CompInfo[] | null,
  onCompInfosChange: (compInfos: EasyTech.CompInfo[]) => void,
  onChange: () => void,
  isReordering: boolean
}
) {
  const dispatch = useDrawerLayoutDispatch();

  const [allCompInfo, setAllCompInfo] = useState<EasyTech.CompInfo[] | null>(null);
  const [createCompInfo, setCreateCompInfo] = useState<EasyTech.CompInfo[] | null>(null);
  const [duringCompInfo, setDuringCompInfo] = useState<EasyTech.CompInfo[] | null>(null);
  const [afterCompInfo, setAfterCompInfo] = useState<EasyTech.CompInfo[] | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const TABLE_HEAD = [
    {
      label: "order",
      orderKey: "order"
    },{
      label: "label",
      orderKey: "label"
    },{
      label: "type",
      orderKey: "type"
    },{
      label: "value",
      orderKey: "value"
    },{
      label: "mandatory",
      orderKey: "mandatory"
    },{
      label: "active",
      orderKey: "active"
    },{
      label: t("common:invoice"),
      orderKey: null
    },{
      label: "action",
      orderKey: null
    }
  ];

  /*
   * Kept in step with TABLE_HEAD on purpose: CompInfosListItem renders the same cells in both modes,
   * so a column added to one array and not the other shifts every value one place to the left.
   */
  const TABLE_HEAD_REORDER = [
    {
      label: "order",
      orderKey: "order"
    },{
      label: "usage",
      orderKey: "usage"
    },{
      label: "label",
      orderKey: "label"
    },{
      label: "type",
      orderKey: "type"
    },{
      label: "value",
      orderKey: "value"
    },{
      label: "mandatory",
      orderKey: "mandatory"
    },{
      label: "active",
      orderKey: "active"
    },{
      label: t("common:invoice"),
      orderKey: null
    }
  ];


  // Configuration DnD
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Gère la fin d'un drag-and-drop
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || !compInfos) return;
    
    if (active.id !== over.id) {
      const oldIndex = compInfos.findIndex(item => item.id === active.id);
      const newIndex = compInfos.findIndex(item => item.id === over.id);
      
      const newItems = arrayMove(compInfos, oldIndex, newIndex);
      onCompInfosChange(newItems);
    }
  };

  /**
  * handleCustomerCreate: handle customer creation on fly
  * @param data - customer data 
  */
  const handleCompInfo = (data: EasyTech.CompInfo, created: boolean): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!data.type)
        return;

      if (data.translations) {
        data.translations = data.translations.filter((el: EasyTech.Translation) => {
          const cond = !el.label;
          return !cond;
        });
      }
      if (data.choices) {
        data.choices.forEach((choice) => {
          // Un id vide n'est pas une clé : le back essaie de l'enregistrer tel quel et répond 500.
          if (!choice.id) delete choice.id;
          if (choice.translations)
            choice.translations = choice.translations.filter((el: EasyTech.Translation) => {
              const cond = !el.label;
              return !cond;
            });
        });
      }
      const select = ["SELECT", "CHECKBOX", "RADIO", "MULTIPLESELECT"].includes(data.type);
      api.request(created ? "POST" : "PUT", "admin/compinfos/"+(select ? "selects" : "inputs")+(created ? "" : "/"+data.id), data)
        .then((ret) => {
          toast.success(created ? t("admin:compinfos:createSuccess") : t("admin:compinfos:updateSuccess"));
          let newItems = structuredClone(compInfos);
          if (newItems === null) {
            newItems = [];
          }
          if (created)
            newItems.push(ret as EasyTech.CompInfo);
          else {
            const i = newItems.findIndex((v) => v.id === data.id);
            newItems[i] = data;
          }
          onCompInfosChange(newItems);
          closeDrawer();
          return resolve();
        }).catch((err) => {
          console.error(err);
          showApiErrorToast(err, { fallbackMessage: t("admin:compinfos:saveError") });
          return reject();
        });
    });
  };
  /***/
  
  /**
  * openEditDrawer - Open drawer to customer edit 
  */
  const openEditDrawer = (comp: EasyTech.CompInfo) => {
    dispatch({
      type: "open",
      title: t("Edit comp info"),
      content: <DrawerForm  data={comp}
                            onSubmit={(e) => handleCompInfo(e, false)}
                            closeDrawer={closeDrawer}
                            FormComponent={CompInfoForm}/>
    });
  };
  /***/

  /**
  * openCreateDrawer - Open drawer to customer create 
  */
  const openCreateDrawer = () => {
    dispatch({
      type: "open",
      title: t("Create comp info"),
      /*
       * sendToInvoice is seeded explicitly rather than left undefined: a new compInfo stays off the
       * customer's invoice until someone opts it in. It matches the column default for new rows —
       * compInfos that predate the flag were backfilled to true to keep existing invoices intact,
       * so do not "align" this to true by reasoning from those.
       */
      content: <DrawerForm  data={{service: "WORKORDER", "usage": "ALL", "translations": [], "label": "new", constraints: "OPTIONAL", order: 0, sendToInvoice: false} as EasyTech.CompInfo}
                            onSubmit={(e) => handleCompInfo(e, true)}
                            closeDrawer={closeDrawer}
                            FormComponent={CompInfoForm}/>
    });
  };
  /***/
  
    
  /**
  * closeDrawer - close drawer 
  */
  const closeDrawer = () => {
    dispatch({ type: "close" });
  };
  /***/

  /**
   * useEffect: get compinfos list
   */
  useEffect(() => {
    if (compInfos === null)
      return;
    const listed = (...usages: string[]) => compInfos.filter((compInfo) =>
      usages.includes(compInfo.usage || "") && (showDisabled || compInfo.enabled === true));
    setAllCompInfo(listed("ALL"));
    setCreateCompInfo(listed("CREATE", "CREATE_AFTER", "CREATE_DURING", "ALL"));
    setDuringCompInfo(listed("DURING", "DURING_AFTER", "CREATE_DURING", "ALL"));
    setAfterCompInfo(listed("AFTER", "CREATE_AFTER", "DURING_AFTER", "ALL"));
  }, [compInfos, showDisabled]);
  /***/

  const reorderCompInfos = (compInfos ?? []).filter((e) => showDisabled || e.enabled === true);

  return (
    <div>
      <div className="flex items-center gap-4">
        <Button
          variant="filled"
          color="blue"
          className="normal-case font-thin"
          onClick={openCreateDrawer}
        >
          {t("admin:compinfos:createButton")}
        </Button>
        <div className="flex items-start gap-3">
          <Typography>{t("admin:compinfos:showDisabled")}</Typography>
          <div className="form-value">
            <Switch checked={showDisabled}
              onChange={(e) => setShowDisabled(e)}/>
          </div>
        </div>
      </div>
      {compInfos ? (
        isReordering ? (
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={reorderCompInfos.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table tableHead={TABLE_HEAD_REORDER}>
                {reorderCompInfos.map((c, i) => (
                  <CompInfosListItem
                    key={c.id}
                    compInfo={c}
                    isLast={i === reorderCompInfos.length - 1}
                    isReordering={isReordering}
                    onChange={onChange}
                    index={i + 1}
                  />
                ))}
              </Table>
            </SortableContext>
          </DndContext>
        ) : (
          <>
            {allCompInfo && allCompInfo.length > 0 &&
              <div className="my-6">
                <div className="flex">
                  <label>ALL</label>

                  <span className="text-sm text-gray-500 mt-1">
                    {t("display when creating the intervention, while it is in progress, and once it is closed")}
                  </span>
                </div>

                <Table tableHead={TABLE_HEAD}>
                  {allCompInfo.map((c, i) => (
                    <CompInfosListItem
                      key={c.id}
                      compInfo={c}
                      isLast={i === allCompInfo.length - 1}
                      index={i + 1}
                      onClick={() => openEditDrawer(c)}
                      onChange={onChange}
                    />
                  ))}
                </Table>
              </div>
            }
            {createCompInfo && createCompInfo.length > 0 &&
              <div className="my-6">
                <div className="flex">
                  <label>{t("CREATE")}</label>

                  <span className="text-sm text-gray-500 mt-1">
                    {t("display only when creating the intervention")}
                  </span>
                </div>

                <Table tableHead={TABLE_HEAD}>
                  {createCompInfo.map((c, i) => (
                    <CompInfosListItem  
                      key={c.id}
                      compInfo={c}
                      isLast={i === createCompInfo.length - 1}
                      index={i + 1}
                      onClick={() => openEditDrawer(c)}
                      onChange={onChange}
                    />
                  ))}
                </Table>

              </div>
            }
            {duringCompInfo && duringCompInfo.length > 0 &&
              <div className="my-6">
                <div className="flex gap-2">
                  <label>{t("DURING")}</label>

                  <span className="text-sm text-gray-500 mt-1">
                    {t("display and edit while the intervention is in progress")}
                  </span>
                </div>

                <Table tableHead={TABLE_HEAD}>
                  {duringCompInfo.map((c, i) => (
                    <CompInfosListItem  
                      key={c.id}
                      compInfo={c}
                      isLast={i === duringCompInfo.length - 1}
                      index={i + 1}
                      onClick={() => openEditDrawer(c)}
                      onChange={onChange}
                    />
                  ))}
                </Table>
              </div>
            }
            {afterCompInfo && afterCompInfo.length > 0 &&
              <div className="my-6">
                <div className="flex gap-2">
                  <label>{t("AFTER")}</label>

                  <span className="text-sm text-gray-500 mt-1">
                    {t("display and edit once the intervention is closed")}
                  </span>
                </div>

                <Table tableHead={TABLE_HEAD}>
                  {afterCompInfo.map((c, i) => (
                    <CompInfosListItem
                      key={c.id}
                      compInfo={c}
                      isLast={i === afterCompInfo.length - 1}
                      index={i + 1}
                      onClick={() => openEditDrawer(c)}
                      onChange={onChange}
                    />
                  ))}
                </Table>
              </div>
            }
          </>
        )
      ) : (
        <Loading />
      )}
    </div>
  );
}
/***/
